import React, { useState } from "react";
import dayjs from "dayjs";
import HoverCard from "components/DesignSystem/HoverCard/HoverCard";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { BigExclamation } from "components/icons/BigExclamation";
import { Task1120Tracker } from "types/Models/TaskTracker";
import {
  useSetUrgentFlagMutation,
  useRemoveUrgentFlagMutation,
} from "store/apis/Task1120Tracker";
import BankWarningCircle from "static/images/BankWarningCircle.svg";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import { STATUS_FILED } from "components/TaskTracker/Task1120FilterOptions";

const scoreColor = (
  score: number | null
): "red" | "yellow" | "green" | "gray" => {
  if (score === null) return "gray";
  if (score >= 70) return "red";
  if (score >= 40) return "yellow";
  return "green";
};

const urgencyLabel = (score: number): string => {
  if (score >= 70) return "High urgency";
  if (score >= 40) return "Medium urgency";
  return "Low urgency";
};

const DARK_CARD_CLASS =
  "!t-bg-neutral-100 !t-border-neutral-80 !t-text-surface";

const MarkNotUrgentModal = ({
  open,
  onOpenChange,
  onConfirm,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isLoading: boolean;
}) => (
  <Modal.Root open={open} onOpenChange={onOpenChange}>
    <Modal.Content
      size="small"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <StatusModal
        type="danger"
        title="Mark filing as not urgent?"
        showCloseButton={false}
      >
        <div className="t-text-body-sm t-text-text-30">
          Are you sure you want to mark this filing as not urgent?
        </div>
      </StatusModal>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button>Go back</Button>
        </Modal.RawClose>
        <Button customType="primary" isLoading={isLoading} onClick={onConfirm}>
          Yes, proceed
        </Button>
      </Modal.FooterButtonGroup>
    </Modal.Content>
  </Modal.Root>
);

export const PriorityScore = ({ row }: { row: Task1120Tracker }) => {
  const [markUrgentOpen, setMarkUrgentOpen] = useState(false);
  const [removeUrgentOpen, setRemoveUrgentOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [setUrgentFlag, { isLoading: isSettingUrgent }] =
    useSetUrgentFlagMutation();
  const [removeUrgentFlag, { isLoading: isRemovingUrgent }] =
    useRemoveUrgentFlagMutation();
  const { alertToast, successToast } = useToast();

  const handleMarkUrgentSubmit = async () => {
    try {
      await setUrgentFlag({ task_id: row.task_uuid, reason }).unwrap();
      setMarkUrgentOpen(false);
      setReason("");
      successToast({ message: "Filing marked as urgent" });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const handleMarkUrgentOpenChange = (open: boolean) => {
    setMarkUrgentOpen(open);
    if (!open) setReason("");
  };

  const handleRemoveUrgentConfirm = async () => {
    try {
      await removeUrgentFlag({ task_id: row.task_uuid }).unwrap();
      setRemoveUrgentOpen(false);
      successToast({ message: "Urgent flag removed" });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  if (row.is_urgent) {
    return (
      <>
        <HoverCard.Root openDelay={200}>
          <HoverCard.Trigger asChild>
            <span className="t-cursor-default">
              <Tag tagType="red" rounded icon={false}>
                <span className="t-flex t-items-center t-gap-1">
                  <img
                    src={BankWarningCircle}
                    alt="BankWarningCircle"
                    className="t-size-3"
                  />
                  Urgent
                </span>
              </Tag>
            </span>
          </HoverCard.Trigger>
          <HoverCard.Portal>
            <HoverCard.Content
              side="left"
              align="center"
              className={`t-w-60 t-p-4 t-flex t-flex-col t-gap-3 ${DARK_CARD_CLASS}`}
            >
              <div className="t-flex t-flex-col t-gap-1">
                <p className="t-text-subtitle-sm t-font-semibold t-text-surface">
                  Filing marked urgent
                </p>
                {row.urgent_reason && (
                  <p className="t-text-body-sm t-text-neutral-30">
                    Reason: {row.urgent_reason}
                  </p>
                )}
              </div>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setRemoveUrgentOpen(true);
                }}
              >
                Mark not urgent
              </Button>
            </HoverCard.Content>
          </HoverCard.Portal>
        </HoverCard.Root>

        <MarkNotUrgentModal
          open={removeUrgentOpen}
          onOpenChange={setRemoveUrgentOpen}
          onConfirm={handleRemoveUrgentConfirm}
          isLoading={isRemovingUrgent}
        />
      </>
    );
  }

  const detail = row.priority_score_detail;
  const waitDays = row.wait_days;
  const taskDeadline = dayjs(detail?.deadline_date).endOf("day");
  const today = dayjs().endOf("day");
  const daysLeft = today.diff(taskDeadline, "day");
  const daysToDeadlineLabel =
    daysLeft <= 0
      ? `${daysLeft * -1} working days to deadline`
      : `Overdue by ${daysLeft} working days`;
  const isFiled = row.task_1120_tracker_global_status === STATUS_FILED;

  return (
    <>
      <HoverCard.Root openDelay={200}>
        <HoverCard.Trigger asChild>
          <span className="t-cursor-default">
            <Tag tagType={scoreColor(row.priority_score)} rounded>
              {row.priority_score === null ? "-" : row.priority_score}
            </Tag>
          </span>
        </HoverCard.Trigger>
        <HoverCard.Portal>
          <HoverCard.Content
            side="left"
            align="center"
            className={`t-w-56 t-p-4 t-flex t-flex-col t-gap-2 ${DARK_CARD_CLASS}`}
          >
            {row.priority_score !== null && (
              <p className="t-text-subtitle-sm t-font-semibold t-text-surface">
                {urgencyLabel(row.priority_score)}
              </p>
            )}
            <div className="t-flex t-flex-col t-gap-1 t-text-body-sm t-text-neutral-30">
              {detail && (
                <>
                  <span>{daysToDeadlineLabel}</span>
                  <span>Includes {row.sub_filings.length} form(s)</span>
                </>
              )}
              {waitDays !== null && (
                <span>Customer waiting since {waitDays} days</span>
              )}
            </div>
            {!isFiled && (
              <Button
                customType="danger"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setMarkUrgentOpen(true);
                }}
              >
                Mark urgent
              </Button>
            )}
          </HoverCard.Content>
        </HoverCard.Portal>
      </HoverCard.Root>

      <Modal.Root
        open={markUrgentOpen}
        onOpenChange={handleMarkUrgentOpenChange}
      >
        <Modal.Content
          size="small"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <Modal.Header>
            <Modal.Title>Mark Filing Urgent</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body>
            <div className="t-flex t-flex-col t-gap-1.5">
              <div className="t-flex t-flex-col t-gap-1">
                <label className="t-text-body-sm t-font-medium t-text-text-60">
                  Reason <span className="t-text-red-50">*</span>
                </label>
                <textarea
                  placeholder="Enter the reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={4}
                  className="t-w-full t-resize-none t-rounded t-border t-border-solid t-border-neutral-10 t-bg-surface-lighter-grey t-p-3 t-text-body-sm t-text-text-100 t-transition-all focus:t-border-purple focus:t-bg-surface-transparent focus:t-outline-none hover:t-border-purple-30 hover:t-bg-surface-grey"
                />
              </div>
              <p className="t-text-body-sm t-text-text-30">
                We will send a slack alert to the preparer
              </p>
            </div>
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button onClick={() => setReason("")}>Go back</Button>
            </Modal.RawClose>
            <Button
              customType="primary"
              disabled={!reason.trim() || isSettingUrgent}
              isLoading={isSettingUrgent}
              onClick={handleMarkUrgentSubmit}
            >
              Submit &amp; mark urgent
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
